/**
 * Excel Loader Module
 * Handles loading and parsing Excel files (.xls, .xlsx) with coordinate data
 */

import * as XLSX from 'xlsx';

// Common coordinate field name variations (case-insensitive)
const LATITUDE_FIELDS = [
    'latitude', 'lat', 'latt', 'latitd', 'latitud', 'latit',
    'y', 'y_coord', 'ycoord', 'y_coordinate', 'y-coord',
    'northing', 'north',
    'point_y', 'point-y', 'pointy',
    'geo_lat', 'geo-lat', 'geolat',
    'lat_dd', 'lat-dd', 'latdd',
    'latitude_dd', 'latitude-dd',
    'dec_lat', 'dec-lat', 'declat'
];

const LONGITUDE_FIELDS = [
    'longitude', 'long', 'lon', 'lng', 'longitud', 'longi', 'longt',
    'x', 'x_coord', 'xcoord', 'x_coordinate', 'x-coord',
    'easting', 'east',
    'point_x', 'point-x', 'pointx',
    'geo_lon', 'geo-lon', 'geolon', 'geo_lng', 'geo-lng', 'geolng',
    'lon_dd', 'lon-dd', 'londd', 'lng_dd', 'lng-dd', 'lngdd',
    'longitude_dd', 'longitude-dd',
    'dec_lon', 'dec-lon', 'declon', 'dec_lng', 'dec-lng', 'declng'
];

/**
 * Parse Excel file into rows
 * @param {ArrayBuffer} buffer - Excel file buffer
 * @returns {Object} Parsed data with headers and rows
 */
function parseExcel(buffer) {
    // Read workbook
    const workbook = XLSX.read(buffer, { type: 'array' });

    // Get first sheet
    const sheetName = workbook.SheetNames[0];
    if (!sheetName) {
        throw new Error('Excel file has no sheets');
    }

    const worksheet = workbook.Sheets[sheetName];

    // Convert to JSON (array of objects)
    const jsonData = XLSX.utils.sheet_to_json(worksheet, {
        header: 1, // Get raw array data
        defval: '' // Default value for empty cells
    });

    if (jsonData.length === 0) {
        throw new Error('Excel sheet is empty');
    }

    // First row is headers
    const headers = jsonData[0].map(h => String(h).trim());

    // Remaining rows are data
    const rows = [];
    for (let i = 1; i < jsonData.length; i++) {
        const values = jsonData[i];
        if (values.length > 0) {
            const row = {};
            headers.forEach((header, index) => {
                row[header] = values[index] !== undefined ? String(values[index]).trim() : '';
            });
            rows.push(row);
        }
    }

    return { headers, rows };
}

/**
 * Detect coordinate fields in headers with flexible matching
 * @param {Array} headers - Excel headers
 * @returns {Object} Object with latField and lonField
 */
function detectCoordinateFields(headers) {
    let latField = null;
    let lonField = null;

    // Helper function to normalize field names
    const normalize = (str) => str.toLowerCase().trim().replace(/[\s_-]/g, '');

    // Strategy 1: Exact match (case-insensitive)
    for (const header of headers) {
        const normalized = normalize(header);

        if (!latField && LATITUDE_FIELDS.some(field => normalize(field) === normalized)) {
            latField = header;
        }

        if (!lonField && LONGITUDE_FIELDS.some(field => normalize(field) === normalized)) {
            lonField = header;
        }

        if (latField && lonField) break;
    }

    // Strategy 2: Partial match - contains key terms
    if (!latField || !lonField) {
        for (const header of headers) {
            const normalized = normalize(header);

            // Look for latitude indicators
            if (!latField) {
                if (normalized.includes('lat') ||
                    normalized === 'y' ||
                    normalized.includes('north')) {
                    latField = header;
                }
            }

            // Look for longitude indicators
            if (!lonField) {
                if (normalized.includes('lon') ||
                    normalized.includes('lng') ||
                    normalized === 'x' ||
                    normalized.includes('east')) {
                    lonField = header;
                }
            }

            if (latField && lonField) break;
        }
    }

    // Strategy 3: Numeric field detection for simple X/Y or lat/lon pairs
    if (!latField || !lonField) {
        const numericFields = headers.filter((header, index) => {
            const normalized = normalize(header);
            // Check if field name suggests coordinates
            return normalized.length <= 3 &&
                   (normalized === 'x' || normalized === 'y' ||
                    normalized.includes('lat') || normalized.includes('lon') ||
                    /^[0-9]+$/.test(normalized)); // numeric column names
        });

        if (numericFields.length >= 2 && (!latField || !lonField)) {
            // If we have fields like "0" and "1" or simple numeric indices
            if (!latField && !lonField) {
                latField = numericFields[1]; // Usually Y/lat comes second
                lonField = numericFields[0]; // Usually X/lon comes first
            }
        }
    }

    return { latField, lonField };
}

/**
 * Convert Excel data to GeoJSON
 * @param {Object} excelData - Parsed Excel data
 * @param {string} latField - Latitude field name
 * @param {string} lonField - Longitude field name
 * @returns {Object} GeoJSON FeatureCollection
 */
function excelToGeoJSON(excelData, latField, lonField) {
    const features = [];

    for (let i = 0; i < excelData.rows.length; i++) {
        const row = excelData.rows[i];
        const lat = parseFloat(row[latField]);
        const lon = parseFloat(row[lonField]);

        // Skip rows with invalid coordinates
        if (isNaN(lat) || isNaN(lon)) {
            console.warn(`Row ${i + 1} has invalid coordinates, skipping`);
            continue;
        }

        // Validate WGS84 bounds
        if (lat < -90 || lat > 90 || lon < -180 || lon > 180) {
            console.warn(`Row ${i + 1} has out-of-bounds coordinates (${lat}, ${lon}), skipping`);
            continue;
        }

        // Create properties (all columns except coordinates)
        const properties = { ...row };

        // Add feature index
        properties.__featureIndex = features.length;

        const feature = {
            type: 'Feature',
            geometry: {
                type: 'Point',
                coordinates: [lon, lat] // GeoJSON uses [longitude, latitude]
            },
            properties
        };

        features.push(feature);
    }

    return {
        type: 'FeatureCollection',
        features
    };
}

/**
 * Load and parse Excel file
 * @param {File} file - Excel file (.xls or .xlsx)
 * @param {Function} progressCallback - Progress callback (progress, message)
 * @returns {Object} Result with geojson, fileName, and featureCount
 */
export async function loadExcel(file, progressCallback) {
    try {
        progressCallback(10, 'Reading Excel file...');

        // Read file as ArrayBuffer
        const buffer = await file.arrayBuffer();

        progressCallback(30, 'Parsing Excel data...');

        // Parse Excel
        const excelData = parseExcel(buffer);

        if (excelData.rows.length === 0) {
            throw new Error('No data rows found in Excel file');
        }

        progressCallback(50, 'Detecting coordinate fields...');

        // Detect coordinate fields
        const { latField, lonField } = detectCoordinateFields(excelData.headers);

        if (!latField || !lonField) {
            const availableFields = excelData.headers.join(', ');
            throw new Error(
                `Could not detect coordinate fields.\n\n` +
                `Available fields: ${availableFields}\n\n` +
                `Looking for: ${LATITUDE_FIELDS.join(', ')} and ${LONGITUDE_FIELDS.join(', ')}`
            );
        }

        console.log(`Detected coordinates: ${latField} (latitude), ${lonField} (longitude)`);

        progressCallback(70, 'Converting to GeoJSON...');

        // Convert to GeoJSON
        const geojson = excelToGeoJSON(excelData, latField, lonField);

        if (geojson.features.length === 0) {
            throw new Error('No valid features could be created from Excel data');
        }

        progressCallback(90, 'Processing complete...');

        return {
            geojson,
            fileName: file.name.replace(/\.(xls|xlsx)$/i, ''),
            featureCount: geojson.features.length,
            coordinateFields: { latField, lonField }
        };

    } catch (error) {
        console.error('Error loading Excel file:', error);
        throw error;
    }
}
